<template>
  <div class="console-card img-panel">
    <div>
      <h3 class="img-title">Upload images</h3>
      <p class="img-hint">Adds files to this email's image folder so a section can use them.</p>
    </div>

    <div
      class="img-drop"
      :class="{ over: isDragOver }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
    >
      <UIcon name="i-heroicons-cloud-arrow-up" class="h-8 w-8" style="color:var(--muted);" />
      <div>Drop images here</div>
      <AppButton small @click="fileInput?.click()">Choose files</AppButton>
      <div class="img-hint">Up to 10 MB each.</div>
      <input
        ref="fileInput"
        type="file"
        multiple
        hidden
        accept="image/jpeg,image/png,image/gif,image/webp"
        @change="handleFileSelection"
      >
    </div>

    <div v-if="selectedFiles.length > 0">
      <div class="console-label" style="margin-bottom:6px;">Ready to upload</div>
      <ul class="img-list">
        <li v-for="(file, index) in selectedFiles" :key="index">
          <span class="truncate" style="flex:1;">{{ file.name }}</span>
          <span class="mono" style="color:var(--muted); font-size:13px;">{{ formatFileSize(file.size) }}</span>
          <AppButton icon variant="ghost" :aria-label="`Remove ${file.name}`" @click="removeFile(index)">
            <UIcon name="i-heroicons-x-mark" class="h-4 w-4" />
          </AppButton>
        </li>
      </ul>
    </div>

    <div v-if="uploading && uploadProgress > 0">
      <div class="flex justify-between" style="color:var(--muted); font-size:13px;">
        <span>Uploading</span>
        <span class="mono">{{ uploadProgress }}%</span>
      </div>
      <div class="img-progress"><div :style="{ width: uploadProgress + '%' }" /></div>
    </div>

    <div v-if="uploadedFiles.length > 0">
      <div class="console-label" style="margin-bottom:6px; color:var(--good);">Uploaded</div>
      <ul class="img-list">
        <li v-for="file in uploadedFiles" :key="file.key">
          <span class="truncate" style="flex:1;">{{ file.originalName }}</span>
          <AppButton small variant="ghost" @click="copyUrl(file.savedPath)">Copy URL</AppButton>
          <AppButton small variant="ghost" @click="openImage(file.savedPath)">Open</AppButton>
        </li>
      </ul>
    </div>

    <div class="flex flex-wrap justify-end gap-2">
      <AppButton :disabled="uploading" @click="clearAll">Clear</AppButton>
      <AppButton variant="primary" :disabled="selectedFiles.length === 0 || uploading" @click="uploadFiles">
        {{ uploading ? 'Uploading…' : `Upload ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}` }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  groupAlias: {
    type: String,
    required: true
  },
  emailAlias: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['uploaded', 'error'])

// State
const selectedFiles = ref<File[]>([])
const uploadedFiles = ref<Array<{ originalName: string; savedPath: string; key: string }>>([])
const uploading = ref(false)
const uploadProgress = ref(0)
const isDragOver = ref(false)

// File input ref
const fileInput = ref<HTMLInputElement>()

// Helper functions
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  else return (bytes / 1048576).toFixed(1) + ' MB'
}

function validateFile(file: File): boolean {
  const maxSize = 10 * 1024 * 1024 // 10MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  if (file.size > maxSize) {
    useToast().add({
      title: 'File too large',
      description: `${file.name} is larger than 10MB`,
      color: 'error'
    })
    return false
  }

  if (!allowedTypes.includes(file.type)) {
    useToast().add({
      title: 'Invalid file type',
      description: `${file.name} is not a supported image format`,
      color: 'error'
    })
    return false
  }

  return true
}

function addFiles(files: FileList | File[]) {
  const newFiles = Array.from(files).filter(validateFile)

  // Check for duplicates
  const existingNames = selectedFiles.value.map(f => f.name)
  const uniqueFiles = newFiles.filter(file => !existingNames.includes(file.name))

  if (uniqueFiles.length !== newFiles.length) {
    useToast().add({
      title: 'Duplicate files',
      description: 'Some files were already selected and skipped',
      color: 'yellow'
    })
  }

  selectedFiles.value.push(...uniqueFiles)
}

// Event handlers
function handleFileSelection(event: Event): void {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length > 0) {
    addFiles(input.files)
  }
}

function handleDrop(event: DragEvent): void {
  isDragOver.value = false
  if (event.dataTransfer?.files) {
    addFiles(event.dataTransfer.files)
  }
}

function removeFile(index: number): void {
  selectedFiles.value.splice(index, 1)
}

function clearAll(): void {
  selectedFiles.value = []
  uploadedFiles.value = []
  uploadProgress.value = 0
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function copyUrl(url: string): void {
  navigator.clipboard.writeText(url)
  useToast().add({
    title: 'Copied',
    description: 'Image URL copied to clipboard',
    color: 'success'
  })
}

function openImage(url: string): void {
  window.open(url, '_blank')
}

async function uploadFiles(): Promise<void> {
  if (selectedFiles.value.length === 0) return

  try {
    uploading.value = true
    uploadProgress.value = 0

    // Create FormData
    const formData = new FormData()
    selectedFiles.value.forEach(file => {
      formData.append('images', file)
    })

    // Upload with progress tracking
    const response = await $fetch(`/api/v1/images/${props.groupAlias}/${props.emailAlias}/upload`, {
      method: 'POST',
      body: formData,
      onUploadProgress: (progress) => {
        uploadProgress.value = Math.round((progress.loaded / progress.total) * 100)
      }
    })

    if (response.success) {
      uploadedFiles.value.push(...response.files)
      selectedFiles.value = []
      uploadProgress.value = 100

      useToast().add({
        title: 'Upload successful',
        description: `${response.files.length} file${response.files.length !== 1 ? 's' : ''} uploaded successfully`,
        color: 'success'
      })

      emit('uploaded', response.files)

      // Clear file input
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }
  } catch (error) {
    console.error('Error uploading files:', error)
    const errorMessage = error instanceof Error ? error.message : 'Failed to upload files'

    useToast().add({
      title: 'Upload failed',
      description: errorMessage,
      color: 'error'
    })

    emit('error', error)
  } finally {
    uploading.value = false
    uploadProgress.value = 0
  }
}
</script>

<style scoped>
.img-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 16px;
  border: 1px dashed var(--line2);
  border-radius: 14px;
  text-align: center;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.img-drop.over {
  border-color: var(--accentline);
  background: var(--panel2);
}
.img-progress {
  height: 6px;
  margin-top: 6px;
  border-radius: 999px;
  background: var(--raise);
  overflow: hidden;
}
.img-progress > div {
  height: 100%;
  background: var(--accent);
}
</style>
